import { useState, useCallback, useEffect, useRef } from 'react';
import { fetchPerson, searchPersons, searchTeams } from '../api';

/**
 * Custom hook for managing person form modal state and interactions.
 * Handles form state, suggestions search, and modal positioning/dragging.
 *
 * @returns {Object} Person form state and handlers
 */
export default function usePersonForm() {
  // Modal visibility and position
  const [showPersonModal, setShowPersonModal] = useState(false);
  const [personModalPos, setPersonModalPos] = useState({ x: 0, y: 0 });
  const personModalDrag = useRef({
    active: false,
    startX: 0,
    startY: 0,
    startPosX: 0,
    startPosY: 0,
  });

  // Form state
  const [personForm, setPersonForm] = useState({
    prefix: '',
    first_name: '',
    last_name: '',
    suffix: '',
    jersey_number: '',
    team_id: '',
  });
  const [modalPersonId, setModalPersonId] = useState(null);
  const [personSuggestions, setPersonSuggestions] = useState([]);
  const [suggestTimer, setSuggestTimer] = useState(null);

  // Team search state
  const [teamSearchQuery, setTeamSearchQuery] = useState('');
  const [teamSuggestions, setTeamSuggestions] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamSearchTimer, setTeamSearchTimer] = useState(null);

  // Open person modal, optionally with existing person data
  const openPersonModal = useCallback(async ({ face = null, personId = null } = {}) => {
    setModalPersonId(personId || (face ? face.person : null));
    // Reset team search state
    setTeamSearchQuery('');
    setTeamSuggestions([]);
    setSelectedTeam(null);

    if (personId || face?.person) {
      try {
        const p = await fetchPerson(personId || face.person);
        // Get first team membership if any
        const firstMembership = p.team_memberships?.[0];
        setPersonForm({
          prefix: p.prefix || '',
          first_name: p.first_name || '',
          last_name: p.last_name || '',
          suffix: p.suffix || '',
          jersey_number: p.jersey_number ?? '',
          team_id: firstMembership?.team_id || '',
        });
        // Set selected team for display
        if (firstMembership?.team_id) {
          setSelectedTeam({
            id: firstMembership.team_id,
            name: firstMembership.team_name,
          });
          setTeamSearchQuery(firstMembership.team_name || '');
        }
      } catch (e) {
        console.error(e);
      }
    } else {
      setPersonForm({
        prefix: '',
        first_name: '',
        last_name: '',
        suffix: '',
        jersey_number: '',
        team_id: '',
      });
    }

    setShowPersonModal(true);
    setPersonModalPos({ x: 0, y: 0 }); // Reset position when opening
  }, []);

  // Close person modal
  const closePersonModal = useCallback(() => {
    setShowPersonModal(false);
  }, []);

  // Handle modal drag start
  const handleModalDragStart = useCallback(
    (e) => {
      // Only start drag from the header, not from buttons
      if (e.target.closest('button')) return;
      e.preventDefault();
      personModalDrag.current = {
        active: true,
        startX: e.clientX,
        startY: e.clientY,
        startPosX: personModalPos.x,
        startPosY: personModalPos.y,
      };
    },
    [personModalPos]
  );

  // Search for person suggestions with debounce
  // eslint-disable-next-line react-hooks/set-state-in-effect -- Debounced search pattern
  useEffect(() => {
    const q = `${personForm.first_name || ''} ${personForm.last_name || ''}`.trim();

    if (suggestTimer) {
      clearTimeout(suggestTimer);
    }

    if (!q || q.length < 2) {
      setPersonSuggestions([]);
      setSuggestTimer(null);
      return;
    }

    const t = setTimeout(async () => {
      try {
        const res = await searchPersons(q);
        setPersonSuggestions(res || []);
      } catch (e) {
        console.error(e);
      }
    }, 250);

    setSuggestTimer(t);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personForm.first_name, personForm.last_name]);

  // Search for team suggestions with debounce
  // eslint-disable-next-line react-hooks/set-state-in-effect -- Debounced search pattern
  useEffect(() => {
    if (teamSearchTimer) {
      clearTimeout(teamSearchTimer);
    }

    // Don't search if query matches selected team (user selected, not searching)
    if (selectedTeam && teamSearchQuery === selectedTeam.name) {
      setTeamSuggestions([]);
      setTeamSearchTimer(null);
      return;
    }

    if (!teamSearchQuery || teamSearchQuery.length < 2) {
      setTeamSuggestions([]);
      setTeamSearchTimer(null);
      return;
    }

    const t = setTimeout(async () => {
      try {
        const res = await searchTeams(teamSearchQuery);
        setTeamSuggestions(res || []);
      } catch (e) {
        console.error(e);
      }
    }, 250);

    setTeamSearchTimer(t);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamSearchQuery, selectedTeam]);

  // Select a team from suggestions
  const selectTeam = useCallback((team) => {
    setSelectedTeam(team);
    setTeamSearchQuery(team.name);
    setTeamSuggestions([]);
    setPersonForm((prev) => ({ ...prev, team_id: team.id }));
  }, []);

  // Clear team selection
  const clearTeam = useCallback(() => {
    setSelectedTeam(null);
    setTeamSearchQuery('');
    setTeamSuggestions([]);
    setPersonForm((prev) => ({ ...prev, team_id: '' }));
  }, []);

  // Handle modal drag movement
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!personModalDrag.current.active) return;
      const dx = e.clientX - personModalDrag.current.startX;
      const dy = e.clientY - personModalDrag.current.startY;
      setPersonModalPos({
        x: personModalDrag.current.startPosX + dx,
        y: personModalDrag.current.startPosY + dy,
      });
    };

    const handleMouseUp = () => {
      personModalDrag.current.active = false;
    };

    if (showPersonModal) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [showPersonModal]);

  return {
    // State
    showPersonModal,
    setShowPersonModal,
    personModalPos,
    setPersonModalPos,
    personForm,
    setPersonForm,
    modalPersonId,
    setModalPersonId,
    personSuggestions,

    // Team search state
    teamSearchQuery,
    setTeamSearchQuery,
    teamSuggestions,
    selectedTeam,

    // Handlers
    openPersonModal,
    closePersonModal,
    handleModalDragStart,
    selectTeam,
    clearTeam,
  };
}
